import { supabase } from '@/utils/supabase';
import type { Investor, EpoRule, EpdRule, DocumentCategory, InvestorDocument, InvestorOperationalContact, InvestorNetWorthRow } from '@/types/investor';
import { createComponentLogger } from '@/utils/logger';
import { DocumentService } from './documentService';
import { normalizeInvestorProducts } from '@/utils/investor/productUtils';
import { hiddenRolesFromAllowedRoles } from '@/utils/investor/documentVisibility';

const log = createComponentLogger('InvestorService', 'database');

export interface ArchiveInvestorResult {
  success: true;
  investorId: string;
  investorName: string;
  archivedAt: string;
  alreadyArchived: boolean;
}

export interface ArchivedInvestorSummary {
  id: string;
  investorName: string;
  archivedAt: string;
}

function mapDocumentHiddenRoles(doc: { hidden_roles?: string[] | null; allowed_roles?: string[] | null }): string[] | undefined {
  if (Array.isArray(doc.hidden_roles)) {
    return doc.hidden_roles;
  }
  return hiddenRolesFromAllowedRoles(doc.allowed_roles);
}

/**
 * Allowlist for `updateInvestorFields` — the ONLY columns a scoped section save may
 * write. Maps a camelCase Investor key to its snake_case `investors` column. Anything
 * not listed here is ignored, so a partial/optimistic patch can never clobber an
 * unrelated column (unlike the whole-record `convertToDatabase` path).
 */
const EDITABLE_INVESTOR_FIELD_COLUMNS: Record<string, string> = {
  investor: 'investor_name',
  // Status / approval / channel enablement (coupled values are computed by the caller)
  status: 'wholesale_status',
  approvedAsOfDate: 'wholesale_approved_date',
  originalApprovedAsOfDate: 'original_wholesale_approved_date',
  correspondentStatus: 'correspondent_status',
  correspondentApprovedAsOfDate: 'correspondent_approved_date',
  originalCorrespondentApprovedAsOfDate: 'original_correspondent_approved_date',
  correspondentLockedAt: 'correspondent_locked_at',
  canDoWholesale: 'can_do_wholesale',
  canGoCorrespondent: 'can_go_correspondent',
  // delegatedType intentionally NOT writable — legacy read-only fallback; delegation is authoritative (item 6).
  delegation: 'delegation',
  // Products
  wholesaleProducts: 'wholesale_products',
  correspondentProducts: 'correspondent_products',
  // Compensation / fees
  wholesaleLenderPaidComp: 'wholesale_lender_paid_comp',
  wholesaleAdditionalLendwiseFee: 'wholesale_additional_lendwise_fee',
  // Processing portal access
  wholesaleWebsite: 'wholesale_website',
  correspondentWebsite: 'correspondent_website',
  // Closing portal access (independent from Processing)
  closingWholesaleWebsite: 'closing_wholesale_website',
  closingCorrespondentWebsite: 'closing_correspondent_website',
  // Account Executive contacts (inline-editable from the roster)
  wholesaleAeName: 'wholesale_ae_name',
  wholesaleAePhone: 'wholesale_ae_phone',
  wholesaleAeEmail: 'wholesale_ae_email',
  correspondentAeName: 'correspondent_ae_name',
  correspondentAePhone: 'correspondent_ae_phone',
  correspondentAeEmail: 'correspondent_ae_email',
  website: 'legacy_website',
  aeName: 'legacy_ae_name',
  aePhone: 'legacy_ae_phone',
  aeEmail: 'legacy_ae_email',
  // Notes
  wholesaleNotes: 'wholesale_notes',
  correspondentNotes: 'correspondent_notes',
  // EPO/EPD + repurchase
  wholesaleEpoRule: 'wholesale_epo_rule',
  wholesaleEpdRule: 'wholesale_epd_rule',
  correspondentEpoRule: 'correspondent_epo_rule',
  correspondentEpdRule: 'correspondent_epd_rule',
  repurchaseRule: 'repurchase_rule',
  // Optimal Blue
  wholesaleCanIntegrateOptimalBlue: 'wholesale_can_integrate_optimal_blue',
  wholesaleHasIntegratedOptimalBlue: 'wholesale_has_integrated_optimal_blue',
  correspondentCanIntegrateOptimalBlue: 'correspondent_can_integrate_optimal_blue',
  correspondentHasIntegratedOptimalBlue: 'correspondent_has_integrated_optimal_blue',
  // Manage Investors v2: Processing/Closing fields
  howToGetCredentials: 'how_to_get_credentials',
  portalNotes: 'portal_notes',
  closingHowToGetCredentials: 'closing_how_to_get_credentials',
  closingPortalNotes: 'closing_portal_notes',
  fhaLenderId: 'fha_lender_id',
  vaLenderId: 'va_lender_id',
  mersOrgId: 'mers_org_id',
  taxId: 'tax_id',
  docPlanCodes: 'doc_plan_codes',
  submissionInstructions: 'submission_instructions',
  submissionNotes: 'submission_notes',
  deliveryInstructions: 'delivery_instructions',
  deliveryNotes: 'delivery_notes',
  deliveryMethod: 'delivery_method',
  deliveryMethodOther: 'delivery_method_other',
  closingRelianceLetterRequired: 'closing_reliance_letter_required',
  // Clarifii reliance-letter config (consumed live by send-clarifii-reliance)
  clarifiRelianceRequired: 'clarifi_reliance_required',
  clarifiRelianceInvestorRecipients: 'clarifi_reliance_investor_recipients',
  operationalContacts: 'operational_contacts',
  closingOperationalContacts: 'closing_operational_contacts',
  netWorthRequirementRows: 'net_worth_requirement_rows'
};

// Postgres date/timestamptz columns reject '' — coerce blanks to NULL like the
// whole-record path does with `|| null`.
const DATE_INVESTOR_FIELD_KEYS = new Set([
  'approvedAsOfDate',
  'originalApprovedAsOfDate',
  'correspondentApprovedAsOfDate',
  'originalCorrespondentApprovedAsOfDate',
  'correspondentLockedAt'
]);

// Actionable copy for known investors CHECK constraints. Unmapped constraints
// surface their name so failures stay diagnosable instead of hiding behind a
// generic "invalid data" toast (a masked constraint was misread as a
// date-format bug in the field).
const INVESTOR_CHECK_CONSTRAINT_MESSAGES: Record<string, string> = {
  investors_wholesale_comp_range: 'Lender Paid Comp must be between 1.00% and 2.50%',
  investors_at_least_one_channel: 'Enable at least one channel (Wholesale or Correspondent)',
  investors_wholesale_status_check: 'Wholesale status must be Approved, Pending, or Inactive',
  investors_correspondent_status_check: 'Correspondent status must be Approved, Pending, or Inactive',
  investors_net_worth_optional_nonnegative_chk: 'Net worth values cannot be negative',
  investors_delegated_type_optional_chk: 'Delegation type must be Delegated, Non-Delegated, or Delegated/Non-Delegated'
};

const checkViolationMessage = (pgMessage: string | undefined): string => {
  const constraint = pgMessage?.match(/violates check constraint "([^"]+)"/)?.[1];
  if (constraint && INVESTOR_CHECK_CONSTRAINT_MESSAGES[constraint]) {
    return INVESTOR_CHECK_CONSTRAINT_MESSAGES[constraint];
  }
  return constraint
    ? `Save blocked by data rule "${constraint}". Please review the fields and try again`
    : 'Invalid data format. Please check all fields and try again';
};

/**
 * Database column interface for investors table
 * Maps snake_case database columns to proper types
 */
interface DatabaseInvestor {
  servicer_selection_mode?: 'product_scope' | 'per_loan';
  id: string;
  organization_id: string;
  investor_name: string;
  status?: 'draft' | 'active';  // Draft/active status in database
  draft_created_at?: string | null;  // Draft support (null clears it on publish)
  draft_last_saved_at?: string | null;  // Draft support
  can_do_wholesale: boolean;
  can_go_correspondent: boolean;
  wholesale_products: string[] | null;
  correspondent_products: string[] | null;
  doc_type: string | null;
  wholesale_lender_paid_comp: number | null;
  wholesale_additional_lendwise_fee: number | null;
  wholesale_status: string;
  wholesale_approved_date: string | null;
  original_wholesale_approved_date: string | null;
  correspondent_status: string;
  correspondent_approved_date: string | null;
  correspondent_locked_at: string | null;
  original_correspondent_approved_date: string | null;
  delegated_type: string | null;
  delegation: { delegated: string[]; nonDelegated: string[] } | null;
  net_worth_delegated: number | null;
  net_worth_non_delegated: number | null;
  wholesale_ae_name: string | null;
  wholesale_ae_phone: string | null;
  wholesale_ae_email: string | null;
  correspondent_ae_name: string | null;
  correspondent_ae_phone: string | null;
  correspondent_ae_email: string | null;
  legacy_ae_name: string | null;
  legacy_ae_phone: string | null;
  legacy_ae_email: string | null;
  wholesale_website: string | null;
  wholesale_username: string | null;
  wholesale_password: string | null;
  correspondent_website: string | null;
  correspondent_username: string | null;
  correspondent_password: string | null;
  closing_wholesale_website?: string | null;
  closing_correspondent_website?: string | null;
  legacy_website: string | null;
  legacy_admin_username: string | null;
  legacy_password: string | null;
  wholesale_can_integrate_optimal_blue: boolean | null;
  wholesale_has_integrated_optimal_blue: boolean | null;
  correspondent_can_integrate_optimal_blue: boolean | null;
  correspondent_has_integrated_optimal_blue: boolean | null;
  wholesale_epo_rule: EpoRule | null; // JSONB
  correspondent_epo_rule: EpoRule | null; // JSONB
  wholesale_epd_rule: EpdRule | null; // JSONB
  correspondent_epd_rule: EpdRule | null; // JSONB
  repurchase_rule: string | null;
  legacy_epo_rule: string | null;
  legacy_epd_rule: string | null;
  wholesale_notes: string | null;
  correspondent_notes: string | null;
  legacy_notes: string | null;
  clarifi_reliance_required: boolean;
  clarifi_reliance_investor_recipients: string[];
  // Manage Investors v2: Processing/Closing operational fields
  how_to_get_credentials?: string | null;
  portal_notes?: string | null;
  closing_how_to_get_credentials?: string | null;
  closing_portal_notes?: string | null;
  fha_lender_id?: string | null;
  va_lender_id?: string | null;
  mers_org_id?: string | null;
  tax_id?: string | null;
  doc_plan_codes?: string | null;
  submission_instructions?: string | null;
  submission_notes?: string | null;
  delivery_instructions?: string | null;
  delivery_notes?: string | null;
  delivery_method?: string | null;
  delivery_method_other?: string | null;
  closing_reliance_letter_required?: boolean | null;
  operational_contacts?: unknown; // JSONB array
  closing_operational_contacts?: unknown; // JSONB array
  net_worth_requirement_rows?: unknown; // JSONB array
  created_by: string | null;
  updated_by: string | null;
  assigned_officer_id: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  // Nested relations loaded via Supabase joins
  updated_by_user?: {
    full_name: string | null;
  } | null;
  investor_history?: Array<{
    id: string;
    action: string;
    details: string;
    change_data: unknown;
    user_id: string;
    created_at: string;
  }>;
  investor_documents?: Array<{
    id: string;
    investor_id: string;
    document_type: string;
    category: string;
    original_file_name: string;
    display_name: string | null;
    file_url: string;
    file_size: number;
    mime_type: string;
    version: number;
    is_active: boolean;
    notes: string | null;
    uploaded_by: string;
    uploaded_at: string;
    storage_path?: string | null;
    storage_bucket?: string | null;
    content?: string | null;
    extraction_status?: InvestorDocument['extractionStatus'] | null;
    extraction_error?: string | null;
    extraction_started_at?: string | null;
    extracted_at?: string | null;
    total_pages?: number | null;
    extraction_time_ms?: number | null;
    sub_type?: string | null;
    file_hash?: string | null;
    effective_date?: string | null;
    // Manage Investors v2 gating metadata (other doc fields are read loosely as before)
    hidden_roles?: string[] | null;
    allowed_roles?: string[] | null;
    linked_tabs?: string[] | null;
  }>;
}

export class InvestorService {
  static async setServicerSelectionMode(investorId: string, mode: 'product_scope' | 'per_loan'): Promise<{ id: string; servicerSelectionMode: 'product_scope' | 'per_loan' }> {
    const { data, error } = await supabase.rpc('set_investor_servicer_selection_mode', {
      p_investor_id: investorId, p_mode: mode
    }).single<{ id: string; servicer_selection_mode: 'product_scope' | 'per_loan' }>();
    if (error) {
      throw new Error(error.message);
    }
    return { id: data.id, servicerSelectionMode: data.servicer_selection_mode };
  }

  /**
   * Parse docType from database - handles JSONB arrays and legacy string values
   */
  private static parseDocType(value: unknown): string[] | undefined {
    if (!value) {
return undefined;
}

    // If it's already an array (from JSONB), return as is
    if (Array.isArray(value)) {
      return value;
    }

    // If it's an object (JSONB parsed), check if it's an array
    if (typeof value === 'object' && value !== null) {
      return Array.isArray(value) ? value : undefined;
    }

    // If it's a string, try parsing as JSON
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        // If JSON.parse fails, it's a legacy plain string value
        return [value];
      }
    }

    return undefined;
  }

  /**
   * Convert TypeScript Investor model to database format
   * Handles field name mapping and structure flattening
   */
  private static convertToDatabase(investor: Investor, organizationId: string, userId?: string): Partial<DatabaseInvestor> {
    return {
      organization_id: organizationId,
      investor_name: investor.investor,
      can_do_wholesale: investor.canDoWholesale,
      can_go_correspondent: investor.canGoCorrespondent,
      wholesale_products: normalizeInvestorProducts(investor.wholesaleProducts).length > 0
        ? normalizeInvestorProducts(investor.wholesaleProducts)
        : null,
      correspondent_products: normalizeInvestorProducts(investor.correspondentProducts).length > 0
        ? normalizeInvestorProducts(investor.correspondentProducts)
        : null,
      doc_type: investor.docType && investor.docType.length > 0 ? investor.docType : null, // Now stored as JSONB array
      wholesale_lender_paid_comp: investor.wholesaleLenderPaidComp ?? null,
      wholesale_additional_lendwise_fee: investor.wholesaleAdditionalLendwiseFee ?? null,
      wholesale_status: investor.status || 'Pending',
      wholesale_approved_date: investor.approvedAsOfDate || null,
      original_wholesale_approved_date: investor.originalApprovedAsOfDate || null,
      correspondent_status: investor.correspondentStatus || 'Inactive',
      correspondent_approved_date: investor.correspondentApprovedAsOfDate || null,
      correspondent_locked_at: investor.correspondentLockedAt || null,
      original_correspondent_approved_date: investor.originalCorrespondentApprovedAsOfDate || null,
      // delegated_type is legacy/read-only (item 6) — never written by new flows so existing values
      // remain as a display fallback; delegation is authoritative.
      delegation: investor.delegation ?? { delegated: [], nonDelegated: [] },
      net_worth_delegated: investor.netWorthRequirements?.delegated ?? null,  // Use ?? to preserve 0 values
      net_worth_non_delegated: investor.netWorthRequirements?.nonDelegated ?? null,  // Use ?? to preserve 0 values
      wholesale_ae_name: investor.wholesaleAeName || null,
      wholesale_ae_phone: investor.wholesaleAePhone || null,
      wholesale_ae_email: investor.wholesaleAeEmail || null,
      correspondent_ae_name: investor.correspondentAeName || null,
      correspondent_ae_phone: investor.correspondentAePhone || null,
      correspondent_ae_email: investor.correspondentAeEmail || null,
      legacy_ae_name: investor.aeName || null,
      legacy_ae_phone: investor.aePhone || null,
      legacy_ae_email: investor.aeEmail || null,
      wholesale_website: investor.wholesaleWebsite || null,
      wholesale_username: investor.wholesaleUsername || null,
      wholesale_password: investor.wholesalePassword || null, // TODO: Encrypt in production
      correspondent_website: investor.correspondentWebsite || null,
      correspondent_username: investor.correspondentUsername || null,
      correspondent_password: investor.correspondentPassword || null, // TODO: Encrypt in production
      closing_wholesale_website: investor.closingWholesaleWebsite || null,
      closing_correspondent_website: investor.closingCorrespondentWebsite || null,
      legacy_website: investor.website || null,
      legacy_admin_username: investor.adminUsername || null,
      legacy_password: investor.password || null, // TODO: Encrypt in production
      wholesale_can_integrate_optimal_blue: investor.wholesaleCanIntegrateOptimalBlue || false,
      wholesale_has_integrated_optimal_blue: investor.wholesaleHasIntegratedOptimalBlue || false,
      correspondent_can_integrate_optimal_blue: investor.correspondentCanIntegrateOptimalBlue || false,
      correspondent_has_integrated_optimal_blue: investor.correspondentHasIntegratedOptimalBlue || false,
      wholesale_epo_rule: investor.wholesaleEpoRule || null,
      correspondent_epo_rule: investor.correspondentEpoRule || null,
      wholesale_epd_rule: investor.wholesaleEpdRule || null,
      correspondent_epd_rule: investor.correspondentEpdRule || null,
      repurchase_rule: investor.repurchaseRule || null,
      legacy_epo_rule: investor.epoRule || null,
      legacy_epd_rule: investor.epdRule || null,
      wholesale_notes: investor.wholesaleNotes || null,
      correspondent_notes: investor.correspondentNotes || null,
      legacy_notes: investor.notes || null,
      clarifi_reliance_required: investor.clarifiRelianceRequired || false,
      clarifi_reliance_investor_recipients: investor.clarifiRelianceInvestorRecipients || [],
      closing_how_to_get_credentials: investor.closingHowToGetCredentials || null,
      closing_portal_notes: investor.closingPortalNotes || null,
      created_by: userId || null,
      updated_by: userId || null
    };
  }

  /**
   * Convert database format to TypeScript Investor model
   * Handles field name mapping and structure composition
   */
  private static convertFromDatabase(dbInvestor: DatabaseInvestor): Investor {
    return {
      id: dbInvestor.id,
      investor: dbInvestor.investor_name,
      servicerSelectionMode: dbInvestor.servicer_selection_mode ?? 'product_scope',
      // Merge wholesale and correspondent products, removing duplicates
      productType: normalizeInvestorProducts([
        ...(dbInvestor.wholesale_products || []),
        ...(dbInvestor.correspondent_products || [])
      ]),
      docType: InvestorService.parseDocType(dbInvestor.doc_type),
      canDoWholesale: dbInvestor.can_do_wholesale,
      canGoCorrespondent: dbInvestor.can_go_correspondent,
      wholesaleProducts: normalizeInvestorProducts(dbInvestor.wholesale_products || []),
      correspondentProducts: normalizeInvestorProducts(dbInvestor.correspondent_products || []),
      wholesaleLenderPaidComp: dbInvestor.wholesale_lender_paid_comp ?? null,
      wholesaleAdditionalLendwiseFee: dbInvestor.wholesale_additional_lendwise_fee ?? null,
      status: dbInvestor.wholesale_status as 'Approved' | 'Pending' | 'Inactive',
      approvedAsOfDate: dbInvestor.wholesale_approved_date || undefined,
      originalApprovedAsOfDate: dbInvestor.original_wholesale_approved_date || undefined,
      correspondentStatus: dbInvestor.correspondent_status as 'Approved' | 'Pending' | 'Inactive',
      correspondentApprovedAsOfDate: dbInvestor.correspondent_approved_date || undefined,
      correspondentLockedAt: dbInvestor.correspondent_locked_at || undefined,
      originalCorrespondentApprovedAsOfDate: dbInvestor.original_correspondent_approved_date || undefined,
      delegatedType: dbInvestor.delegated_type as 'Delegated' | 'Non-Delegated' | 'Delegated/Non-Delegated' | undefined,
      delegation: dbInvestor.delegation ?? { delegated: [], nonDelegated: [] },
      netWorthRequirements: {
        delegated: dbInvestor.net_worth_delegated ?? undefined,
        nonDelegated: dbInvestor.net_worth_non_delegated ?? undefined
      },
      wholesaleAeName: dbInvestor.wholesale_ae_name || '',
      wholesaleAePhone: dbInvestor.wholesale_ae_phone || '',
      wholesaleAeEmail: dbInvestor.wholesale_ae_email || '',
      correspondentAeName: dbInvestor.correspondent_ae_name || '',
      correspondentAePhone: dbInvestor.correspondent_ae_phone || '',
      correspondentAeEmail: dbInvestor.correspondent_ae_email || '',
      aeName: dbInvestor.legacy_ae_name || '',
      aePhone: dbInvestor.legacy_ae_phone || '',
      aeEmail: dbInvestor.legacy_ae_email || '',
      wholesaleWebsite: dbInvestor.wholesale_website || undefined,
      wholesaleUsername: dbInvestor.wholesale_username || undefined,
      wholesalePassword: dbInvestor.wholesale_password || undefined,
      correspondentWebsite: dbInvestor.correspondent_website || undefined,
      correspondentUsername: dbInvestor.correspondent_username || undefined,
      correspondentPassword: dbInvestor.correspondent_password || undefined,
      closingWholesaleWebsite: dbInvestor.closing_wholesale_website || undefined,
      closingCorrespondentWebsite: dbInvestor.closing_correspondent_website || undefined,
      website: dbInvestor.legacy_website || '',
      adminUsername: dbInvestor.legacy_admin_username || '',
      password: dbInvestor.legacy_password || '',
      wholesaleCanIntegrateOptimalBlue: dbInvestor.wholesale_can_integrate_optimal_blue || false,
      wholesaleHasIntegratedOptimalBlue: dbInvestor.wholesale_has_integrated_optimal_blue || false,
      correspondentCanIntegrateOptimalBlue: dbInvestor.correspondent_can_integrate_optimal_blue || false,
      correspondentHasIntegratedOptimalBlue: dbInvestor.correspondent_has_integrated_optimal_blue || false,
      wholesaleEpoRule: dbInvestor.wholesale_epo_rule || { type: 'payments', value: 0, description: '' },
      wholesaleEpdRule: dbInvestor.wholesale_epd_rule || { type: 'payments', value: 0, description: '' },
      correspondentEpoRule: dbInvestor.correspondent_epo_rule || { type: 'payments', value: 0, description: '' },
      correspondentEpdRule: dbInvestor.correspondent_epd_rule || { type: 'payments', value: 0, description: '' },
      repurchaseRule: dbInvestor.repurchase_rule || '',
      epoRule: dbInvestor.legacy_epo_rule || undefined,
      epdRule: dbInvestor.legacy_epd_rule || undefined,
      wholesaleNotes: dbInvestor.wholesale_notes || undefined,
      correspondentNotes: dbInvestor.correspondent_notes || undefined,
      notes: dbInvestor.legacy_notes || '',
      clarifiRelianceRequired: dbInvestor.clarifi_reliance_required || false,
      clarifiRelianceInvestorRecipients: dbInvestor.clarifi_reliance_investor_recipients || [],
      // Manage Investors v2: Processing/Closing operational fields
      howToGetCredentials: dbInvestor.how_to_get_credentials || undefined,
      portalNotes: dbInvestor.portal_notes || undefined,
      closingHowToGetCredentials: dbInvestor.closing_how_to_get_credentials || undefined,
      closingPortalNotes: dbInvestor.closing_portal_notes || undefined,
      fhaLenderId: dbInvestor.fha_lender_id || undefined,
      vaLenderId: dbInvestor.va_lender_id || undefined,
      mersOrgId: dbInvestor.mers_org_id || undefined,
      taxId: dbInvestor.tax_id || undefined,
      docPlanCodes: dbInvestor.doc_plan_codes || undefined,
      submissionInstructions: dbInvestor.submission_instructions || undefined,
      submissionNotes: dbInvestor.submission_notes || undefined,
      deliveryInstructions: dbInvestor.delivery_instructions || undefined,
      deliveryNotes: dbInvestor.delivery_notes || undefined,
      deliveryMethod: (dbInvestor.delivery_method as 'Investor Connect' | 'Other' | null | undefined) ?? undefined,
      deliveryMethodOther: dbInvestor.delivery_method_other || undefined,
      closingRelianceLetterRequired: dbInvestor.closing_reliance_letter_required ?? false,
      operationalContacts: Array.isArray(dbInvestor.operational_contacts)
        ? (dbInvestor.operational_contacts as InvestorOperationalContact[])
        : [],
      closingOperationalContacts: Array.isArray(dbInvestor.closing_operational_contacts)
        ? (dbInvestor.closing_operational_contacts as InvestorOperationalContact[])
        : [],
      netWorthRequirementRows: Array.isArray(dbInvestor.net_worth_requirement_rows)
        ? (dbInvestor.net_worth_requirement_rows as InvestorNetWorthRow[])
        : [],
      // Transform history records from database format
      history: (dbInvestor.investor_history || []).map(h => ({
        id: h.id,
        timestamp: h.created_at,
        action: h.action,
        details: h.details,
        userId: h.user_id
      })),
      // Transform documents from database format
      documents: (dbInvestor.investor_documents || []).map(doc => ({
        id: doc.id,
        investorId: doc.investor_id,
        documentType: doc.document_type as 'wholesale' | 'correspondent',
        category: doc.category as DocumentCategory,
        originalFileName: doc.original_file_name,
        displayName: doc.display_name,
        fileUrl: doc.file_url,
        fileSize: doc.file_size,
        mimeType: doc.mime_type,
        uploadedAt: doc.uploaded_at,
        uploadedBy: doc.uploaded_by || 'Unknown',
        isActive: doc.is_active,
        version: doc.version,
        notes: doc.notes || undefined,
        // Storage fields
        storagePath: doc.storage_path ?? undefined,
        storageBucket: doc.storage_bucket ?? undefined,
        // Extraction fields
        content: doc.content ?? undefined,
        extractionStatus: doc.extraction_status ?? undefined,
        extractionError: doc.extraction_error ?? undefined,
        extractionStartedAt: doc.extraction_started_at ?? undefined,
        extractedAt: doc.extracted_at ?? undefined,
        totalPages: doc.total_pages ?? undefined,
        extractionTimeMs: doc.extraction_time_ms ?? undefined,
        // Credit Guidelines sub-type and file hash
        subType: doc.sub_type ?? undefined,
        fileHash: doc.file_hash ?? undefined,
        effectiveDate: doc.effective_date ?? undefined,
        // Manage Investors v2 gating metadata
        hiddenRoles: mapDocumentHiddenRoles(doc),
        allowedRoles: doc.allowed_roles ?? undefined,
        linkedTabs: (doc.linked_tabs ?? undefined) as ('processing' | 'closing')[] | undefined
      })),
      // Map draft/active status from database to draftStatus field
      draftStatus: dbInvestor.status || undefined,  // Draft support
      draftCreatedAt: dbInvestor.draft_created_at || undefined,  // Draft support
      draftLastSavedAt: dbInvestor.draft_last_saved_at || undefined,  // Draft support
      createdAt: dbInvestor.created_at,
      updatedAt: dbInvestor.updated_at,
      deletedAt: dbInvestor.deleted_at || undefined,
      updatedBy: dbInvestor.updated_by || undefined,
      updatedByName: dbInvestor.updated_by_user?.full_name || undefined
    };
  }

  /**
   * Create a new investor in the database
   * @param investor - Investor data to create
   * @param organizationId - Organization ID for multi-tenant isolation
   * @returns Created investor with database-generated fields
   */
  static async createInvestor(investor: Investor, organizationId: string): Promise<Investor> {
    try {
      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;

      const dbInvestor = this.convertToDatabase(investor, organizationId, userId);

      // For new investors being created (not drafts), ensure status is set to 'active' in database
      if (!investor.draftStatus || investor.draftStatus !== 'draft') {
        dbInvestor.status = 'active';
      }

      log.info('Creating investor', { investorName: investor.investor, organizationId });

      const { data, error } = await supabase
        .from('investors')
        .insert(dbInvestor)
        .select()
        .single();

      if (error) {
        log.error('Failed to create investor', { error, investorName: investor.investor });

        // Provide user-friendly error messages
        if (error.code === '23505') { // Unique constraint violation
          throw new Error('An investor with this name already exists in your organization');
        } else if (error.code === '23514') { // Check constraint violation
          throw new Error(checkViolationMessage(error.message));
        } else if (error.message?.includes('doc_type')) {
          throw new Error('Invalid Non-QM document type format. Please select from the dropdown');
        }

        throw new Error(`Failed to create investor: ${error.message || 'Unknown error'}`);
      }

      // Create initial history entry
      await supabase.from('investor_history').insert({
        investor_id: data.id,
        organization_id: organizationId,
        action: 'Created',
        details: 'Investor account created',
        user_id: userId
      });

      log.info('Investor created successfully', { investorId: data.id, investorName: data.investor_name });
      return this.convertFromDatabase(data);
    } catch (error) {
      log.error('Failed to create investor', { error });
      throw error;
    }
  }

  /**
   * Get all investors for an organization (including user's drafts)
   * @param organizationId - Organization ID for filtering
   * @param userId - Optional user ID to include their drafts
   * @returns Array of investors (excludes soft-deleted)
   */
  static async getInvestors(organizationId: string, userId?: string): Promise<Investor[]> {
    try {
      log.debug('Fetching investors', { organizationId, userId });

      // Build the status filter - include active investors and user's own drafts
      let statusFilter = 'status.eq.active,status.is.null';  // Active investors
      if (userId) {
        // Also include drafts created by the current user
        statusFilter = `status.eq.active,status.is.null,and(status.eq.draft,created_by.eq.${userId})`;
      }

      const { data, error } = await supabase
        .from('investors')
        .select(`
          *,
          updated_by_user:users!investors_updated_by_fkey (
            full_name
          ),
          investor_history!investor_history_investor_id_fkey (
            id,
            action,
            details,
            change_data,
            user_id,
            created_at
          ),
          investor_documents!investor_documents_investor_id_fkey (
            id,
            investor_id,
            document_type,
            category,
            sub_type,
            original_file_name,
            display_name,
            file_url,
            file_size,
            mime_type,
            version,
            is_active,
            notes,
            uploaded_by,
            uploaded_at,
            extraction_status,
            effective_date,
            hidden_roles,
            allowed_roles,
            linked_tabs
          )
        `)
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .or(statusFilter)
        .order('created_at', { ascending: false });

      if (error) {
        log.error('Failed to fetch investors', { error, organizationId });
        throw error;
      }

      log.info('Investors fetched successfully', { count: data?.length || 0, organizationId });
      return (data || []).map(this.convertFromDatabase);
    } catch (error) {
      log.error('Failed to fetch investors', { error });
      throw error;
    }
  }

  /**
   * Get a single investor by ID
   * @param investorId - Investor ID
   * @param organizationId - Organization ID for verification
   * @returns Investor or null if not found
   */
  static async getInvestorById(investorId: string, organizationId: string): Promise<Investor | null> {
    try {
      log.debug('Fetching investor by ID', { investorId, organizationId });

      const { data, error } = await supabase
        .from('investors')
        .select(`
          *,
          updated_by_user:users!investors_updated_by_fkey (
            full_name
          ),
          investor_history!investor_history_investor_id_fkey (
            id,
            action,
            details,
            change_data,
            user_id,
            created_at
          ),
          investor_documents!investor_documents_investor_id_fkey (
            id,
            investor_id,
            document_type,
            category,
            original_file_name,
            display_name,
            file_url,
            file_size,
            mime_type,
            version,
            is_active,
            notes,
            uploaded_by,
            uploaded_at,
            storage_path,
            storage_bucket,
            content,
            extraction_status,
            extraction_error,
            extraction_started_at,
            extracted_at,
            total_pages,
            extraction_time_ms,
            sub_type,
            file_hash,
            effective_date,
            hidden_roles,
            allowed_roles,
            linked_tabs
          )
        `)
        .eq('id', investorId)
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Not found
          log.warn('Investor not found', { investorId, organizationId });
          return null;
        }
        log.error('Failed to fetch investor', { error, investorId });
        throw error;
      }

      return this.convertFromDatabase(data);
    } catch (error) {
      log.error('Failed to fetch investor by ID', { error, investorId });
      throw error;
    }
  }

  /** Fetch the minimal data needed by the reversible archived-investors view. */
  static async getArchivedInvestors(organizationId: string): Promise<ArchivedInvestorSummary[]> {
    const { data, error } = await supabase
      .from('investors')
      .select('id, investor_name, deleted_at')
      .eq('organization_id', organizationId)
      .not('deleted_at', 'is', null)
      .order('deleted_at', { ascending: false });

    if (error) {
      log.error('Failed to fetch archived investors', {
        metadata: { error, organizationId }
      });
      throw new Error(error.message || 'Archived investors could not be loaded');
    }

    return (data || []).flatMap((investor) => investor.deleted_at ? [{
      id: investor.id,
      investorName: investor.investor_name,
      archivedAt: investor.deleted_at
    }] : []);
  }

  /**
   * Update an existing investor
   * @param investor - Updated investor data (must include id)
   * @param organizationId - Organization ID for verification
   * @returns Updated investor
   */
  static async updateInvestor(investor: Investor, organizationId: string): Promise<Investor> {
    try {
      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;

      const dbInvestor = this.convertToDatabase(investor, organizationId, userId);

      // If this was a draft, convert it to active
      if (investor.draftStatus === 'draft') {
        dbInvestor.status = 'active';
        dbInvestor.draft_created_at = null;
        dbInvestor.draft_last_saved_at = null;
      }

      log.info('Updating investor', { investorId: investor.id, investorName: investor.investor });

      const { data, error } = await supabase
        .from('investors')
        .update(dbInvestor)
        .eq('id', investor.id)
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .select()
        .single();

      if (error) {
        log.error('Failed to update investor', { error, investorId: investor.id });

        // Provide user-friendly error messages
        if (error.code === '23505') { // Unique constraint violation
          throw new Error('An investor with this name already exists in your organization');
        } else if (error.code === '23514') { // Check constraint violation
          throw new Error(checkViolationMessage(error.message));
        } else if (error.message?.includes('doc_type')) {
          throw new Error('Invalid Non-QM document type format. Please select from the dropdown');
        }

        throw new Error(`Failed to update investor: ${error.message || 'Unknown error'}`);
      }

      log.info('Investor updated successfully', { investorId: data.id });
      return this.convertFromDatabase(data);
    } catch (error) {
      log.error('Failed to update investor', { error });
      throw error;
    }
  }

  /**
   * Scoped section save for the v2 slide-over. Writes ONLY the allow-listed columns
   * present in `fields` (see EDITABLE_INVESTOR_FIELD_COLUMNS) — never a whole record,
   * so it cannot clobber unrelated columns. The caller is responsible for computing
   * coupled values (e.g. correspondentLockedAt when enabling correspondent, approval
   * dates, net-worth resets) and passing them in `fields`.
   *
   * @returns the freshly hydrated Investor (documents/history intact), or null if missing.
   */
  /**
   * Build the snake_case DB patch for a scoped section save. Pure (no I/O) so it can
   * be unit-tested: maps only allow-listed keys present in `fields`, and writes BOTH
   * net-worth columns whenever `netWorthRequirements` is supplied (so `{}` clears them).
   */
  static buildEditableFieldsPatch(fields: Partial<Investor>): Record<string, unknown> {
    const patch: Record<string, unknown> = {};

    for (const [key, column] of Object.entries(EDITABLE_INVESTOR_FIELD_COLUMNS)) {
      if (Object.prototype.hasOwnProperty.call(fields, key)) {
        if (key === 'wholesaleProducts' || key === 'correspondentProducts') {
          const normalized = normalizeInvestorProducts((fields as Record<string, string[] | undefined>)[key]);
          patch[column] = normalized.length > 0 ? normalized : null;
        } else if (DATE_INVESTOR_FIELD_KEYS.has(key)) {
          patch[column] = (fields as Record<string, unknown>)[key] || null;
        } else {
          patch[column] = (fields as Record<string, unknown>)[key] ?? null;
        }
      }
    }

    // Net worth is a nested object on the model but two flat columns in the DB.
    // When the caller supplies netWorthRequirements at all, write BOTH columns
    // (mirroring the whole-record path's `?? null`) so a reset (e.g. disabling
    // correspondent sends `{}`) actually clears the stored values instead of
    // no-opping and leaving stale net worth.
    if (fields.netWorthRequirements) {
      patch.net_worth_delegated = fields.netWorthRequirements.delegated ?? null;
      patch.net_worth_non_delegated = fields.netWorthRequirements.nonDelegated ?? null;
    }

    return patch;
  }

  static async updateInvestorFields(
    investorId: string,
    fields: Partial<Investor>,
    organizationId: string
  ): Promise<Investor | null> {
    try {
      const patch = this.buildEditableFieldsPatch(fields);

      // Nothing editable supplied → no-op, return current state.
      if (Object.keys(patch).length === 0) {
        return this.getInvestorById(investorId, organizationId);
      }

      const { data: { user } } = await supabase.auth.getUser();
      patch.updated_by = user?.id ?? null;

      log.info('Updating investor fields', { investorId, columns: Object.keys(patch) });

      const { error } = await supabase
        .from('investors')
        .update(patch)
        .eq('id', investorId)
        .eq('organization_id', organizationId)
        .is('deleted_at', null);

      if (error) {
        log.error('Failed to update investor fields', { error, investorId });
        if (error.code === '23514') {
          throw new Error(checkViolationMessage(error.message));
        }
        throw new Error(`Failed to save: ${error.message || 'Unknown error'}`);
      }

      return this.getInvestorById(investorId, organizationId);
    } catch (error) {
      log.error('Failed to update investor fields', { error, investorId });
      throw error;
    }
  }

  /**
   * Archive an investor while retaining linked documents, payouts, recons, and
   * audit history. The database RPC owns authorization, locking, and auditing.
   */
  static async archiveInvestor(investorId: string): Promise<ArchiveInvestorResult> {
    log.info('Archiving investor', { metadata: { investorId } });

    const { data, error } = await supabase.rpc('archive_investor', {
      p_investor_id: investorId
    });

    if (error) {
      log.error('Failed to archive investor', {
        metadata: { error, investorId }
      });
      throw new Error(error.message || 'The investor could not be archived');
    }

    const result = data as Partial<ArchiveInvestorResult> | null;
    if (!result?.success || !result.investorId || !result.archivedAt) {
      throw new Error('The database did not confirm the investor archive');
    }

    log.info('Investor archived successfully', {
      metadata: {
        investorId,
        alreadyArchived: result.alreadyArchived
      }
    });
    return result as ArchiveInvestorResult;
  }

  /** Restore a previously archived investor to active investor lists. */
  static async restoreInvestor(investorId: string): Promise<boolean> {
    log.info('Restoring investor', { metadata: { investorId } });

    const { data, error } = await supabase.rpc('restore_investor', {
      investor_id_param: investorId
    });

    if (error) {
      log.error('Failed to restore investor', {
        metadata: { error, investorId }
      });
      throw new Error(error.message || 'The investor could not be restored');
    }

    if (data !== true) {
      throw new Error('The database did not confirm the investor restore');
    }

    log.info('Investor restored successfully', { metadata: { investorId } });
    return true;
  }

  /**
   * Search investors with filters
   * @param organizationId - Organization ID for filtering
   * @param searchTerm - Search term for name/email filtering
   * @returns Array of matching investors
   */
  static async searchInvestors(organizationId: string, searchTerm?: string): Promise<Investor[]> {
    try {
      log.debug('Searching investors', { organizationId, searchTerm });

      let query = supabase
        .from('investors')
        .select('*')
        .eq('organization_id', organizationId)
        .is('deleted_at', null);

      if (searchTerm && searchTerm.trim()) {
        // Use full-text search if available, otherwise use ILIKE
        query = query.or(`investor_name.ilike.%${searchTerm}%,wholesale_ae_email.ilike.%${searchTerm}%,correspondent_ae_email.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        log.error('Failed to search investors', { error, organizationId, searchTerm });
        throw error;
      }

      log.info('Search completed', { count: data?.length || 0, searchTerm });
      return (data || []).map(this.convertFromDatabase);
    } catch (error) {
      log.error('Failed to search investors', { error });
      throw error;
    }
  }

  /**
   * Create a draft investor with minimal required fields
   * @param investor - Partial investor data with required fields
   * @param organizationId - Organization ID
   * @param userId - User ID creating the draft
   * @returns Created draft investor or null on error
   */
  static async createDraft(
    investor: { investor: string; canDoWholesale: boolean; canGoCorrespondent: boolean },
    organizationId: string,
    userId: string
  ): Promise<Investor | null> {
    try {
      log.info('Creating investor draft', { investorName: investor.investor });

      const dbInvestor: Partial<DatabaseInvestor> = {
        investor_name: investor.investor,
        can_do_wholesale: investor.canDoWholesale,
        can_go_correspondent: investor.canGoCorrespondent,
        organization_id: organizationId,
        wholesale_status: investor.canDoWholesale ? 'Pending' : 'Inactive',
        correspondent_status: investor.canGoCorrespondent ? 'Pending' : 'Inactive',
        delegated_type: null,
        net_worth_delegated: null,
        net_worth_non_delegated: null,
        // Mark as draft
        status: 'draft',
        draft_created_at: new Date().toISOString(),
        draft_last_saved_at: new Date().toISOString(),
        created_by: userId
      };

      const { data, error } = await supabase
        .from('investors')
        .insert(dbInvestor)
        .select('*')
        .single();

      if (error) {
        log.error('Failed to create investor draft', { error });
        throw new Error('Failed to create draft');
      }

      return this.convertFromDatabase(data);
    } catch (error) {
      log.error('Failed to create investor draft', { error });
      return null;
    }
  }

  /**
   * Auto-save draft investor data
   * @param investorId - ID of the draft to update
   * @param investorData - Updated investor data
   * @param userId - User ID performing the update
   * @returns Updated draft or null on error
   */
  static async autoSaveDraft(
    investorId: string,
    investorData: Partial<Investor>,
    userId: string
  ): Promise<Investor | null> {
    try {
      // Get org ID first
      const { data: { user } } = await supabase.auth.getUser();
      const organizationId = user?.user_metadata?.organization_id || user?.app_metadata?.organization_id;

      const dbInvestor = this.convertToDatabase(investorData as Investor, organizationId, userId);

      // Add draft timestamp
      dbInvestor.draft_last_saved_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('investors')
        .update(dbInvestor)
        .eq('id', investorId)
        .eq('status', 'draft')
        .eq('created_by', userId)
        .is('deleted_at', null)
        .select('*')
        .single();

      if (error) {
        log.error('Failed to auto-save draft', { error, investorId });
        return null;
      }

      return this.convertFromDatabase(data);
    } catch (error) {
      log.error('Failed to auto-save draft', { error, investorId });
      return null;
    }
  }

  /**
   * Get user's draft investors
   * @param userId - User ID
   * @param organizationId - Organization ID
   * @returns Array of draft investors
   */
  static async getUserDrafts(userId: string, organizationId: string): Promise<Investor[]> {
    try {
      const { data, error } = await supabase
        .from('investors')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('status', 'draft')
        .eq('created_by', userId)
        .is('deleted_at', null)
        .order('draft_last_saved_at', { ascending: false });

      if (error) {
        log.error('Failed to load user drafts', { error });
        return [];
      }

      return data.map(inv => this.convertFromDatabase(inv));
    } catch (error) {
      log.error('Failed to load user drafts', { error });
      return [];
    }
  }

  /**
   * Convert a draft to an active investor
   * @param investorId - Draft investor ID
   * @param userId - User ID performing the conversion
   * @returns true if successful
   */
  static async publishDraft(investorId: string, userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('investors')
        .update({
          status: 'active',
          draft_created_at: null,
          draft_last_saved_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', investorId)
        .eq('status', 'draft')
        .eq('created_by', userId)
        .is('deleted_at', null);

      if (error) {
        log.error('Failed to publish draft', { error, investorId });
        return false;
      }

      log.info('Draft published successfully', { investorId });
      return true;
    } catch (error) {
      log.error('Failed to publish draft', { error, investorId });
      return false;
    }
  }

  /** Archive a draft investor through the same guarded lifecycle. */
  static async deleteDraft(investorId: string, _userId: string): Promise<boolean> {
    try {
      await this.archiveInvestor(investorId);
      log.info('Draft archived successfully', { metadata: { investorId } });
      return true;
    } catch (error) {
      log.error('Failed to archive draft', {
        metadata: { error, investorId }
      });
      return false;
    }
  }

  /**
   * Upload a document for an investor
   * @param investorId - The investor's ID
   * @param file - The file to upload
   * @param category - The document category
   * @param documentType - Whether this is a wholesale or correspondent document
   * @param notes - Optional notes about the document
   * @param organizationId - The organization ID
   * @returns The created document metadata
   */
  static async uploadInvestorDocument(
    investorId: string,
    file: File,
    category: DocumentCategory,
    documentType: 'wholesale' | 'correspondent',
    notes: string | undefined,
    organizationId: string,
    subType?: string,      // For Credit Guidelines
    fileHash?: string,     // For duplicate detection
    effectiveDate?: string, // For Credit Guidelines, Credit Matrix, Seller Guide
    hiddenRoles?: string[],                           // v2 visibility exclusions — carried over on replace
    linkedTabs?: ('processing' | 'closing')[],        // v2 tab linking — carried over on replace
    replaceGroupId?: string,                           // Item 10: when set, adds a new version to this lineage (atomic RPC)
    displayName?: string | null                        // Explicit custom title — carried over on replace
  ): Promise<InvestorDocument> {
    try {
      log.info('Uploading investor document', {
        investorId,
        fileName: file.name,
        category,
        documentType,
        subType
      });

      // Get current user for uploaded_by field
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id || 'system';

      // Item 10: a fresh upload is version 1 in a new lineage; a Replace continues the
      // target lineage (the storage filename uses the next version for path-uniqueness, and
      // the DB version is recomputed atomically inside the replace RPC).
      const version = replaceGroupId
        ? await DocumentService.getNextVersionForGroup(replaceGroupId)
        : 1;

      // Upload the file to storage (pass version to avoid duplicate computation)
      const { url, path } = await DocumentService.uploadDocument(file, {
        investorId,
        organizationId,
        documentType,
        category,
        originalFileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        uploadedBy: userId,
        notes,
        subType,
        fileHash,
        version
      });

      const metadata = {
        investorId,
        organizationId,
        documentType,
        category,
        originalFileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        uploadedBy: userId,
        notes,
        subType,
        fileHash,
        effectiveDate,
        displayName,
        hiddenRoles,
        linkedTabs
      };

      // Replace → atomic archive-current-active + insert-new-version in one transaction.
      // Fresh upload → new lineage (document_group_id defaults), no sibling archiving.
      const document = replaceGroupId
        ? await DocumentService.replaceDocumentViaRpc(replaceGroupId, metadata, url, path)
        : await DocumentService.saveDocumentMetadata(metadata, url, path, version);

      log.info('Investor document uploaded successfully', {
        documentId: document.id,
        category,
        version: document.version
      });

      return document;
    } catch (error) {
      log.error('Failed to upload investor document', { error });
      throw error;
    }
  }

  /**
   * Get all documents for an investor
   * @param investorId - The investor's ID
   * @param documentType - Optional: Filter by wholesale or correspondent
   * @param activeOnly - Whether to return only active documents (default: true)
   * @returns Array of investor documents
   */
  static async getInvestorDocuments(
    investorId: string,
    documentType?: 'wholesale' | 'correspondent',
    activeOnly: boolean = true
  ): Promise<InvestorDocument[]> {
    try {
      log.debug('Fetching investor documents', {
        investorId,
        documentType,
        activeOnly
      });

      const documents = await DocumentService.getInvestorDocuments(
        investorId,
        documentType,
        activeOnly
      );

      log.debug('Fetched investor documents', {
        count: documents.length,
        investorId
      });

      return documents;
    } catch (error) {
      log.error('Failed to get investor documents', { error });
      throw error;
    }
  }

  /**
   * Archive an investor document (set is_active to false)
   * @param documentId - The document ID
   * @param organizationId - The organization ID for security
   */
  static async archiveInvestorDocument(
    documentId: string,
    organizationId: string
  ): Promise<void> {
    try {
      log.info('Archiving investor document', { documentId });

      await DocumentService.archiveDocument(documentId, organizationId);

      log.info('Investor document archived successfully', { documentId });
    } catch (error) {
      log.error('Failed to archive investor document', { error });
      throw error;
    }
  }

  /**
   * Update an investor document's display name
   * @param documentId - The document ID
   * @param displayName - The new display name, or null to use the generated title
   * @param organizationId - The organization ID for security
   */
  static async updateDocumentDisplayName(
    documentId: string,
    displayName: string | null,
    organizationId: string
  ): Promise<void> {
    try {
      log.info('Updating investor document display name', { documentId, displayName });

      await DocumentService.updateDocumentDisplayName(documentId, displayName, organizationId);

      log.info('Investor document display name updated successfully', { documentId });
    } catch (error) {
      log.error('Failed to update investor document display name', { error });
      throw error;
    }
  }

  /**
   * Update the effective date of an investor document
   */
  static async updateDocumentEffectiveDate(
    documentId: string,
    effectiveDate: string | null,
    organizationId: string
  ): Promise<void> {
    try {
      log.info('Updating investor document effective date', { documentId, effectiveDate });

      await DocumentService.updateDocumentEffectiveDate(documentId, effectiveDate, organizationId);

      log.info('Investor document effective date updated successfully', { documentId });
    } catch (error) {
      log.error('Failed to update investor document effective date', { error });
      throw error;
    }
  }

  /**
   * Delete an investor document permanently
   * @param documentId - The document ID
   * @param organizationId - The organization ID for security
   */
  static async deleteInvestorDocument(
    documentId: string,
    organizationId: string
  ): Promise<void> {
    try {
      log.info('Deleting investor document', { documentId });

      await DocumentService.deleteDocument(documentId, organizationId);

      log.info('Investor document deleted successfully', { documentId });
    } catch (error) {
      log.error('Failed to delete investor document', { error });
      throw error;
    }
  }
}
